// React, Next
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

// MUI
import Box from '@mui/material/Box'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Embla
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

// Custom
import StatisticCard, {
  StatisticCardProps
} from '@/components/cards/StatisticCard'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import breakpoints from '@/assets/theme/breakpoints'
import classes from './StatisticCarrousel-CSS'

type ButtonProps = {
  enabled: boolean
  side: 'left' | 'right'
  onClick: () => void
}

function ArrowButton(p: ButtonProps) {
  if (!p.enabled) return null
  return (
    <Box onClick={p.onClick} sx={{ ...classes.emblaButton, [p.side]: '2px' }}>
      {p.side === 'left' ? (
        <ArrowBackIosNewIcon
          sx={{ ...classes.arrowButtonColor, transform: 'scale(0.7)' }}
        />
      ) : (
        <ArrowForwardIosIcon
          sx={{ ...classes.arrowButtonColor, transform: 'scale(0.7)' }}
        />
      )}
    </Box>
  )
}

type Props = {
  cards: StatisticCardProps[]
  /** Autoplay interval in ms. Omit to leave the carousel manual. */
  autoplayDelay?: number
}

export default function StatisticCarrousel(props: Props) {
  const { screenDimensions } = useContext(GlobalContext)

  const autoplay = useRef(
    Autoplay({
      delay: props.autoplayDelay ?? 9000,
      stopOnInteraction: false
    })
  )

  const [viewportRef, embla] = useEmblaCarousel(
    { align: 'start', skipSnaps: false, loop: false },
    props.autoplayDelay === 0 ? [] : [autoplay.current]
  )

  const [prevEnabled, setPrevEnabled] = useState(false)
  const [nextEnabled, setNextEnabled] = useState(false)

  /** How much of the viewport a single card occupies. */
  const getSlideBasis = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopMid) return '33.333%'
    if (w > breakpoints.desktopToMobile) return '50%'
    if (w > breakpoints.mobileMid) return '50%'
    return '100%'
  }

  const scrollPrev = useCallback(() => {
    if (!embla) return
    embla.scrollPrev()
    autoplay.current.reset()
  }, [embla])

  const scrollNext = useCallback(() => {
    if (!embla) return
    embla.scrollNext()
    autoplay.current.reset()
  }, [embla])

  const onSelect = useCallback(() => {
    if (!embla) return
    setPrevEnabled(embla.canScrollPrev())
    setNextEnabled(embla.canScrollNext())
  }, [embla])

  useEffect(() => {
    if (!embla) return
    embla.on('select', onSelect)
    embla.on('reInit', onSelect)
    onSelect()
  }, [embla, onSelect])

  // *** MAIN RENDER ***
  return (
    <Box sx={classes.embla}>
      <ArrowButton side="left" enabled={prevEnabled} onClick={scrollPrev} />
      <ArrowButton side="right" enabled={nextEnabled} onClick={scrollNext} />

      <Box sx={classes.emblaViewport} ref={viewportRef}>
        <Box sx={classes.emblaContainer}>
          {props.cards.map((card, i) => (
            <Box
              key={i}
              sx={{
                ...classes.emblaSlide,
                flex: `0 0 ${getSlideBasis()}`,
                minWidth: 0
              }}
            >
              <StatisticCard {...card} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
